import type { BandSpace, Persisted, Rider } from '@tunasong/schemas'
import { canWriteEntity } from '@tunasong/models'
import { Button, UserAvatar, useNavigateToEntity } from '@tunasong/ui-lib'
import type { FC } from 'react'
import { useState } from 'react'
import { displayName } from '../readiness.js'
import { formatVersionDate } from '../venue-sheet.js'
import { isNudgeSnoozed, snoozeNudge } from './nudge-snooze.js'
import { StageSetupDialog } from './stage-setup-dialog.js'
import { useBandReadiness } from './use-band-readiness.js'
import { useRiderAsks } from './use-rider-asks.js'
import styles from './readiness-card.module.css'

export interface RiderReadinessCardProps {
  band: Persisted<BandSpace>
  /** The band's riders; the card measures against the most recently updated one, and shows only when there is one */
  riders: Persisted<Rider>[]
}

const maxAvatars = 5

/**
 * The band page's readiness card (docs/ux/2026-10-16-rider-collaboration-ux.md): a personal nudge for the viewer, and
 * a readiness line for everyone. The line is status, not a nag: it is never dismissed and collapses when all are
 * ready. Nothing renders until profiles and opt-outs have loaded; a wrong count is worse than none.
 */
export const RiderReadinessCard: FC<RiderReadinessCardProps> = props => {
  const { band, riders } = props
  const { readiness, profiles, viewerId, setPerforming } = useBandReadiness(band, riders)
  const { posting, remind, showLead } = useRiderAsks()
  const navigateToEntity = useNavigateToEntity()
  const [dialogOpen, setDialogOpen] = useState(false)
  /** Not now, for this visit too: storage may be unavailable */
  const [snoozed, setSnoozed] = useState(() => isNudgeSnoozed(band.id, new Date()))

  if (readiness.status !== 'ready' || !viewerId) {
    return null
  }

  const { rider, performers, missing, viewer, viewerUpdatedAt } = readiness
  const bandName = band.name ?? 'the band'
  const riderName = rider.name ?? 'The rider'
  const profileOf = (id: string) => profiles.find(p => p.userId === id) ?? { userId: id }
  const missingNames = missing.map(id => displayName(profileOf(id)))
  const othersMissing = missing.filter(id => id !== viewerId)
  const lead = profileOf(rider.userId)
  const viewerCanSync = rider.userId === viewerId || canWriteEntity(viewerId, rider)
  /** The personal line already offers Open rider: the readiness line does not repeat it */
  const personalOpensRider = viewerCanSync && (viewer === 'drifted' || viewer === 'not-on-rider')

  const total = performers.length
  /** Pluralised on the total; a one-member band names whose setup it is */
  const readinessText = (() => {
    if (missing.length > 0) {
      return `Rider: ${total - missing.length} of ${total} ${total === 1 ? 'setup' : 'setups'} added`
    }
    if (total === 1) {
      return performers[0] === viewerId
        ? 'Your setup is added'
        : `${displayName(profileOf(performers[0]))}'s setup is added`
    }
    return `All ${total} setups added`
  })()

  const handleNotNow = () => {
    snoozeNudge(band.id, new Date())
    setSnoozed(true)
  }

  const askLead = () => {
    if (viewerUpdatedAt) {
      void showLead(rider, viewerId, lead, viewerUpdatedAt)
    }
  }

  const leadAction = viewerCanSync ? (
    <Button size="small" variant="outlined" onClick={() => navigateToEntity(rider)}>
      Open rider
    </Button>
  ) : (
    <Button size="small" variant="outlined" disabled={posting} onClick={askLead}>
      Show {displayName(lead)}
    </Button>
  )

  return (
    <div className={styles.card}>
      {viewer === 'missing' && !snoozed ? (
        <div className={styles.personal}>
          <p className={styles.text}>Add your stage setup so it can go on {bandName}&apos;s rider.</p>
          <div className={styles.actions}>
            <Button size="small" variant="contained" onClick={() => setDialogOpen(true)}>
              Add my setup
            </Button>
            <Button size="small" color="inherit" onClick={() => void setPerforming(false)}>
              I don&apos;t perform with {bandName}
            </Button>
            <Button size="small" color="inherit" onClick={handleNotNow}>
              Not now
            </Button>
          </div>
        </div>
      ) : null}

      {viewer === 'drifted' && viewerUpdatedAt ? (
        <div className={styles.personal}>
          <p className={styles.text}>
            You updated your setup on {formatVersionDate(viewerUpdatedAt)}. {riderName} still has the old one.
          </p>
          <div className={styles.actions}>{leadAction}</div>
        </div>
      ) : null}

      {viewer === 'not-on-rider' ? (
        <div className={styles.personal}>
          <p className={styles.text}>{riderName} doesn&apos;t include your setup yet.</p>
          <div className={styles.actions}>{leadAction}</div>
        </div>
      ) : null}

      {performers.length > 0 ? (
        <div className={styles.line}>
          <p role="status" className={styles.text}>
            {readinessText}
          </p>
          {missing.length > 0 ? (
            <>
              <span className={styles.avatars} aria-hidden="true">
                {missing.slice(0, maxAvatars).map(id => (
                  <UserAvatar key={id} userId={id} size="tiny" link={false} profileCard={false} />
                ))}
              </span>
              <span className={styles.visuallyHidden}>Missing: {missingNames.join(', ')}</span>
              {othersMissing.length > 0 ? (
                <Button
                  size="small"
                  variant="outlined"
                  disabled={posting}
                  onClick={() => void remind(rider, viewerId, othersMissing.map(profileOf))}
                >
                  Remind
                </Button>
              ) : null}
            </>
          ) : personalOpensRider ? null : (
            <Button size="small" variant="outlined" onClick={() => navigateToEntity(rider)}>
              Open rider
            </Button>
          )}
        </div>
      ) : null}

      {viewer === 'not-performing' ? (
        <Button size="small" color="inherit" className={styles.reverse} onClick={() => void setPerforming(true)}>
          I perform with {bandName}
        </Button>
      ) : null}

      <StageSetupDialog
        open={dialogOpen}
        band={{ id: band.id, name: bandName }}
        notPerforming={viewer === 'not-performing'}
        onPerformingChange={performs => void setPerforming(performs)}
        onClose={() => setDialogOpen(false)}
      />
    </div>
  )
}

export default RiderReadinessCard
